import React from 'react';
import {
  Center,
  VStack,
  Button,
  useBreakpointValue,
  Flex,
  Icon,
  Text,
  SimpleGrid,
  Wrap,
} from '@wegroup/design-system';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { chakraCustomScrollBar } from '../../../../common/src/utils/styledComponentsUtils';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import { LoadingSpinner } from 'wg-fe-ui';
import useTracking from '../../../../common/src/hooks/tracking/useTracking';
import { useConversation } from '../../../../conversations/src/features/detail/shared/api/getConversationById';
import { useHistory } from 'react-router';
import { useAdvisoryReportsByParty } from '../../../../advisory-report/src/features/overview/api/getAdvisoryReports';
import { useParty } from '../../../../parties/src/features/detail/api/getParty';
import SmallToolbarWithBack from '@risk-analysis/src/components/recommendations/SmallToolbarWithBack';
import CompanySuccessActionBox from '@risk-analysis/src/pages/recommendations/CompanySuccessActionBox';

const AnvaFinalizePage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const conversationId = useSessionId();
  const { data: convo, isLoading: isLoadingConversation } =
    useConversation(conversationId);
  const partyId = convo?.prospect?.id || '';
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const history = useHistory();

  const { isLoading: isLoadingParty } = useParty(convo?.prospect?.id);
  const { trackEvent } = useTracking();

  const { isLoading: isLoadingReports } = useAdvisoryReportsByParty(
    convo?.prospect?.id,
  );

  const handleViewParty = () => {
    history.push(`/parties/${partyId}`);
  };

  if (isLoadingParty || isLoadingReports || isLoadingConversation)
    return (
      <Center width="100vw" height="100vh" data-test-id="RiskAn_RiskAnalysis">
        <LoadingSpinner />
      </Center>
    );

  return (
    <>
      <Flex direction={{ base: 'column', tablet: 'row' }} h="100%" w="100%">
        {!isMobile && (
          <SmallToolbarWithBack
            tooltipText={t('RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-BUTTON-BACK')}
          />
        )}
        <Center
          h="100%"
          flexGrow={1}
          p="6"
          bg="#FBFBFB"
          overflowY="auto"
          sx={chakraCustomScrollBar}
        >
          <VStack spacing="8" align="stretch" maxW="lg">
            <Text textAlign="center" textStyle="h3Headline">
              {t('COMPANY-SCAN_RECOMMENDATIONS_FINISHED_TITLE')}
            </Text>

            <SimpleGrid
              columns={{
                base: 1,
                laptop: 1,
              }}
              gap="4"
              alignSelf="center"
            >
              <CompanySuccessActionBox
                icon={<Icon name="GeneralOthersAvatar" />}
                title={t('CONVERSATIONS_DETAIL_PARTY-DETAIL_VIEW-PARTY-DETAIL')}
                description={t(
                  'FLOWS_FINALIZE_ANVA_ACTION_PARTY-VIEW_DESCRIPTION',
                )}
                onClick={handleViewParty}
                isLoading={isLoadingParty}
              />
            </SimpleGrid>

            <Wrap
              direction={{ base: 'column', tabletS: 'row' }}
              gridGap="4"
              justify="space-between"
              w="100%"
              alignSelf="center"
            >
              <Button
                as={Link}
                to={`/dashboard`}
                variant="secondary"
                leftIcon={<Icon name="GeneralActionsArrowLeft" />}
                onClick={() => trackEvent('To Dashboard from Anva flow')}
              >
                {t('BACK-TO-DASHBOARD')}
              </Button>
              <Button
                as={Link}
                to={`/conversations/company-scan/${conversationId}`}
                rightIcon={<Icon name="GeneralActionsArrowRight" />}
                onClick={() => trackEvent('To Conversation from Anva flow')}
              >
                {t('VIEW-CONVERSATION')}
              </Button>
            </Wrap>
          </VStack>
        </Center>
      </Flex>
    </>
  );
};

export default AnvaFinalizePage;
